import React from "react";
import {
  Zap,
  Cpu,
  Radio,
  Cable,
  Speaker,
  CircuitBoard,
  MapPin,
  Mail,
  ChevronRight,
  Star,
  Shield,
  Truck,
  Users,
} from "lucide-react";

const stats = [
  { label: "Products Available", value: "500+", icon: CircuitBoard },
  { label: "Happy Customers", value: "10K+", icon: Users },
  { label: "Cities Served", value: "50+", icon: MapPin },
  { label: "Years of Trust", value: "5+", icon: Star },
];

const products = [
  {
    icon: Zap,
    name: "Transformers",
    desc: "High-efficiency transformers for all voltage conversion needs, from small signal to power applications.",
    gradient: "linear-gradient(135deg,#16a34a,#15803d)",
  },
  {
    icon: CircuitBoard,
    name: "SMPS Boards",
    desc: "Compact switched-mode power supply boards delivering stable output with high efficiency ratings.",
    gradient: "linear-gradient(135deg,#eab308,#ca8a04)",
  },
  {
    icon: Cpu,
    name: "Power Supply Modules",
    desc: "Reliable power supply modules engineered for consistent performance in industrial and consumer electronics.",
    gradient: "linear-gradient(135deg,#15803d,#065f46)",
  },
  {
    icon: Speaker,
    name: "Speakers",
    desc: "Crystal-clear audio speakers and driver units for a wide range of acoustic applications.",
    gradient: "linear-gradient(135deg,#facc15,#f59e0b)",
  },
  {
    icon: Radio,
    name: "Electronic Connectors",
    desc: "Premium-grade connectors ensuring secure, low-resistance connections across all your electronic assemblies.",
    gradient: "linear-gradient(135deg,#16a34a,#0d9488)",
  },
];

const values = [
  {
    icon: Shield,
    title: "Quality First",
    desc: "Every component meets stringent quality standards before reaching your hands.",
  },
  {
    icon: Truck,
    title: "Fast Delivery",
    desc: "Reliable pan-India shipping with real-time order tracking.",
  },
  {
    icon: Star,
    title: "Fair Pricing",
    desc: "Transparent pricing with no hidden charges, always competitive.",
  },
];

/* ─── inline styles ─────────────────────────────────────────── */
const s = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#ffffff",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    color: "#111827",
  },

  /* HERO */
  hero: {
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(135deg,#14532d 0%,#166534 50%,#15803d 100%)",
    color: "#ffffff",
  },
  heroBlob1: {
    position: "absolute",
    top: -96,
    left: -96,
    width: 384,
    height: 384,
    borderRadius: "50%",
    background: "#facc15",
    opacity: 0.08,
    filter: "blur(60px)",
    pointerEvents: "none",
  },
  heroBlob2: {
    position: "absolute",
    bottom: -64,
    right: -64,
    width: 320,
    height: 320,
    borderRadius: "50%",
    background: "#fde68a",
    opacity: 0.08,
    filter: "blur(60px)",
    pointerEvents: "none",
  },
  heroInner: {
    position: "relative",
    maxWidth: 1152,
    margin: "0 auto",
    padding: "96px 24px",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 48,
  },
  heroText: { flex: "1 1 320px" },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "rgba(250,204,21,0.15)",
    border: "1px solid rgba(250,204,21,0.35)",
    color: "#fde68a",
    padding: "6px 16px",
    borderRadius: 9999,
    fontSize: 13,
    marginBottom: 20,
  },
  heroH1: {
    fontSize: "clamp(2rem,5vw,3.4rem)",
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: 20,
    color: "#ffffff",
  },
  heroHighlight: { color: "#facc15" },
  heroP: {
    fontSize: 17,
    color: "#bbf7d0",
    lineHeight: 1.7,
    marginBottom: 32,
    maxWidth: 480,
  },
  heroBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "#facc15",
    color: "#14532d",
    padding: "12px 28px",
    borderRadius: 9999,
    fontWeight: 700,
    textDecoration: "none",
    fontSize: 15,
    transition: "background .2s",
    boxShadow: "0 8px 24px rgba(250,204,21,0.35)",
  },
  heroImgWrap: {
    flex: "1 1 300px",
    position: "relative",
  },
  heroImgBox: {
    borderRadius: 16,
    overflow: "hidden",
    border: "4px solid rgba(250,204,21,0.25)",
    boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
  },
  heroImg: { width: "100%", height: 288, objectFit: "cover", display: "block" },
  heroBadgeFloat: {
    position: "absolute",
    bottom: -16,
    left: -16,
    background: "#facc15",
    color: "#14532d",
    borderRadius: 12,
    padding: "12px 16px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    fontWeight: 700,
  },
  heroBadgeNum: { fontSize: 22, fontWeight: 800, display: "block" },
  heroBadgeLbl: { fontSize: 11 },

  /* STATS */
  statsBar: { background: "#166534", color: "#ffffff" },
  statsInner: {
    maxWidth: 1152,
    margin: "0 auto",
    padding: "40px 24px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))",
    gap: 24,
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    textAlign: "center",
  },
  statIconWrap: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "rgba(250,204,21,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: { fontSize: 28, fontWeight: 800, color: "#facc15" },
  statLabel: { fontSize: 13, color: "#bbf7d0" },

  /* MISSION */
  missionSection: { maxWidth: 1152, margin: "0 auto", padding: "80px 24px" },
  missionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
    gap: 48,
    alignItems: "center",
  },
  missionImgWrap: { position: "relative" },
  missionImg: {
    width: "100%",
    height: 320,
    objectFit: "cover",
    borderRadius: 16,
    display: "block",
    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
  },
  missionAccent1: {
    position: "absolute",
    bottom: -20,
    right: -20,
    width: 120,
    height: 120,
    background: "#facc15",
    borderRadius: 16,
    zIndex: -1,
  },
  missionAccent2: {
    position: "absolute",
    top: -20,
    left: -20,
    width: 80,
    height: 80,
    background: "#bbf7d0",
    borderRadius: 12,
    zIndex: -1,
  },
  pill: {
    display: "inline-block",
    background: "#dcfce7",
    color: "#15803d",
    fontSize: 13,
    fontWeight: 600,
    padding: "4px 14px",
    borderRadius: 9999,
    marginBottom: 16,
  },
  pillYellow: {
    display: "inline-block",
    background: "#fef9c3",
    color: "#a16207",
    fontSize: 13,
    fontWeight: 600,
    padding: "4px 14px",
    borderRadius: 9999,
    marginBottom: 16,
  },
  sectionH2: {
    fontSize: "clamp(1.5rem,3vw,2.3rem)",
    fontWeight: 800,
    lineHeight: 1.25,
    color: "#14532d",
    marginBottom: 20,
  },
  highlight: { color: "#eab308" },
  bodyText: { color: "#4b5563", lineHeight: 1.75, marginBottom: 24 },
  valueItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 16,
  },
  valueIconBox: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "#dcfce7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  valueTitle: { fontWeight: 700, color: "#14532d", marginBottom: 2 },
  valueDesc: { fontSize: 13, color: "#6b7280" },

  /* PRODUCTS */
  productsSection: {
    background: "linear-gradient(135deg,#f0fdf4 0%,#fefce8 100%)",
    padding: "80px 0",
  },
  productsInner: { maxWidth: 1152, margin: "0 auto", padding: "0 24px" },
  sectionHead: { textAlign: "center", marginBottom: 56 },
  sectionSubText: {
    color: "#6b7280",
    marginTop: 12,
    maxWidth: 480,
    margin: "12px auto 0",
  },
  productsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))",
    gap: 24,
  },
  productCard: {
    background: "#ffffff",
    borderRadius: 20,
    padding: 24,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    border: "1px solid #d1fae5",
    transition: "box-shadow .25s",
    cursor: "default",
  },
  productIconBox: {
    width: 56,
    height: 56,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  productName: { fontWeight: 700, color: "#14532d", marginBottom: 8 },
  productDesc: { fontSize: 14, color: "#6b7280", lineHeight: 1.65 },
  learnMore: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginTop: 16,
    fontSize: 13,
    fontWeight: 600,
    color: "#16a34a",
  },
  ctaCard: {
    background: "linear-gradient(135deg,#15803d,#14532d)",
    borderRadius: 20,
    padding: 24,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
  },
  ctaIconBox: {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: "rgba(250,204,21,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  ctaTitle: { fontWeight: 700, color: "#ffffff", marginBottom: 8 },
  ctaDesc: { fontSize: 14, color: "#bbf7d0", lineHeight: 1.65 },
  ctaBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 24,
    background: "#facc15",
    color: "#14532d",
    padding: "10px 20px",
    borderRadius: 12,
    fontWeight: 700,
    textDecoration: "none",
    fontSize: 14,
    transition: "background .2s",
  },

  /* CONTACT */
  contactSection: { maxWidth: 1152, margin: "0 auto", padding: "80px 24px" },
  contactGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
    gap: 48,
    alignItems: "center",
  },
  contactCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    padding: 16,
    borderRadius: 14,
    marginBottom: 16,
    border: "1px solid",
  },
  contactCardGreen: { background: "#f0fdf4", borderColor: "#bbf7d0" },
  contactCardYellow: { background: "#fefce8", borderColor: "#fde68a" },
  contactIconGreen: {
    width: 40,
    height: 40,
    borderRadius: 10,
    flexShrink: 0,
    background: "#166534",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  contactIconYellow: {
    width: 40,
    height: 40,
    borderRadius: 10,
    flexShrink: 0,
    background: "#facc15",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  contactLabel: {
    fontWeight: 700,
    fontSize: 14,
    color: "#14532d",
    marginBottom: 4,
  },
  contactVal: { fontSize: 14, color: "#4b5563", lineHeight: 1.6 },
  contactLink: { fontSize: 14, color: "#16a34a", textDecoration: "none" },
  contactImgWrap: { position: "relative" },
  contactImg: {
    width: "100%",
    height: 320,
    objectFit: "cover",
    borderRadius: 16,
    display: "block",
    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
  },
  contactAccent1: {
    position: "absolute",
    top: -16,
    right: -16,
    width: 90,
    height: 90,
    background: "#facc15",
    borderRadius: 14,
    zIndex: -1,
  },
  contactAccent2: {
    position: "absolute",
    bottom: -16,
    left: -16,
    width: 64,
    height: 64,
    background: "#bbf7d0",
    borderRadius: 12,
    zIndex: -1,
  },

  /* FOOTER */
  footer: { background: "#14532d", color: "#ffffff", padding: "28px 24px" },
  footerInner: {
    maxWidth: 1152,
    margin: "0 auto",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  footerBrand: { display: "flex", alignItems: "center", gap: 12 },
  footerLogoBox: {
    width: 36,
    height: 36,
    borderRadius: 8,
    background: "#facc15",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  footerName: { fontWeight: 800, fontSize: 16 },
  footerCopy: { color: "#86efac", fontSize: 13 },
  footerEmail: {
    color: "#facc15",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
  },
};

const AboutUs = ({ onNavigate }) => {
  return (
    <div style={s.page}>
      {/* ── HERO ── */}
      <section style={s.hero}>
        <div style={s.heroBlob1} />
        <div style={s.heroBlob2} />
        <div style={s.heroInner}>
          {/* text */}
          <div style={s.heroText}>
            <div style={s.heroBadge}>
              <Zap size={14} />
              Powering India's Electronics
            </div>
            <h1 style={s.heroH1}>
              About <span style={s.heroHighlight}>Zynventics</span> Technologies
            </h1>
            <p style={s.heroP}>
              An electronics-focused ecommerce platform providing reliable
              electronic components for engineers, hobbyists and businesses
              across India.
            </p>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                onNavigate("contact");
              }}
              style={s.heroBtn}
            >
              Get in Touch <ChevronRight size={16} />
            </a>
          </div>

          {/* image */}
          <div style={s.heroImgWrap}>
            <div style={s.heroImgBox}>
              <img
                src="https://images.unsplash.com/photo-1760842543713-108c3cadbba1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080"
                alt="Electronics components"
                style={s.heroImg}
                onError={(e) => {
                  e.target.style.background = "#166534";
                  e.target.src = "";
                }}
              />
            </div>
            <div style={s.heroBadgeFloat}>
              <span style={s.heroBadgeNum}>500+</span>
              <span style={s.heroBadgeLbl}>Products</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section style={s.statsBar}>
        <div style={s.statsInner}>
          {stats.map(({ label, value, icon: Icon }) => (
            <div key={label} style={s.statItem}>
              <div style={s.statIconWrap}>
                <Icon size={22} color="#facc15" />
              </div>
              <span style={s.statValue}>{value}</span>
              <span style={s.statLabel}>{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── MISSION ── */}
      <section style={s.missionSection}>
        <div style={s.missionGrid}>
          {/* image col */}
          <div style={s.missionImgWrap}>
            <img
              src="https://images.unsplash.com/photo-1743836798811-6208a08233c9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080"
              alt="Engineer working"
              style={s.missionImg}
              onError={(e) => {
                e.target.style.background = "#dcfce7";
                e.target.src = "";
              }}
            />
            <div style={s.missionAccent1} />
            <div style={s.missionAccent2} />
          </div>

          {/* text col */}
          <div>
            <span style={s.pill}>Our Mission</span>
            <h2 style={s.sectionH2}>
              Making Quality Electronics{" "}
              <span style={s.highlight}>Accessible Across India</span>
            </h2>
            <p style={s.bodyText}>
              Our mission is to make high-quality electronic components
              accessible across India at fair prices. We believe every engineer,
              hobbyist, and business deserves the best components without
              breaking the bank.
            </p>
            {values.map(({ icon: Icon, title, desc }) => (
              <div key={title} style={s.valueItem}>
                <div style={s.valueIconBox}>
                  <Icon size={18} color="#16a34a" />
                </div>
                <div>
                  <p style={s.valueTitle}>{title}</p>
                  <p style={s.valueDesc}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRODUCTS ── */}
      <section style={s.productsSection}>
        <div style={s.productsInner}>
          <div style={s.sectionHead}>
            <span style={s.pillYellow}>What We Offer</span>
            <h2 style={{ ...s.sectionH2, textAlign: "center" }}>
              Our Product Range
            </h2>
            <p style={s.sectionSubText}>
              From transformers to connectors — everything you need for your
              next electronics project, all in one place.
            </p>
          </div>

          <div style={s.productsGrid}>
            {products.map(({ icon: Icon, name, desc, gradient }) => (
              <div key={name} style={s.productCard}>
                <div style={{ ...s.productIconBox, background: gradient }}>
                  <Icon size={26} color="#ffffff" />
                </div>
                <p style={s.productName}>{name}</p>
                <p style={s.productDesc}>{desc}</p>
                <span style={s.learnMore}>
                  Learn more <ChevronRight size={14} />
                </span>
              </div>
            ))}

            {/* CTA card */}
            <div style={s.ctaCard}>
              <div>
                <div style={s.ctaIconBox}>
                  <Cable size={26} color="#facc15" />
                </div>
                <p style={s.ctaTitle}>Can't Find What You Need?</p>
                <p style={s.ctaDesc}>
                  Contact us and we'll source the exact component you're looking
                  for.
                </p>
              </div>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate("contact");
                }}
                style={s.ctaBtn}
              >
                Contact Us <ChevronRight size={15} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section style={s.contactSection}>
        <div style={s.contactGrid}>
          {/* text col */}
          <div>
            <span style={s.pill}>Get In Touch</span>
            <h2 style={s.sectionH2}>
              We're Here to <span style={s.highlight}>Help You</span>
            </h2>
            <p style={{ ...s.bodyText, marginBottom: 32 }}>
              Have questions about our products or need bulk pricing? Reach out
              to our team and we'll get back to you promptly.
            </p>

            <div style={{ ...s.contactCard, ...s.contactCardGreen }}>
              <div style={s.contactIconGreen}>
                <MapPin size={18} color="#facc15" />
              </div>
              <div>
                <p style={s.contactLabel}>Address</p>
                <p style={s.contactVal}>
                  Near Youniform World, Gobardanga station road, Ramnagar
                  <br />
                  North 24 Parganas – 743273, West Bengal, India
                </p>
              </div>
            </div>

            <div style={{ ...s.contactCard, ...s.contactCardYellow }}>
              <div style={s.contactIconYellow}>
                <Mail size={18} color="#14532d" />
              </div>
              <div>
                <p style={s.contactLabel}>Email</p>
                <a href="mailto:info@zynventics.com" style={s.contactLink}>
                  info@zynventics.com
                </a>
              </div>
            </div>
          </div>

          {/* image col */}
          <div style={s.contactImgWrap}>
            <img
              src="https://images.unsplash.com/photo-1694532458834-c27d41c40804?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080"
              alt="Electronics manufacturing"
              style={s.contactImg}
              onError={(e) => {
                e.target.style.background = "#dcfce7";
                e.target.src = "";
              }}
            />
            <div style={s.contactAccent1} />
            <div style={s.contactAccent2} />
          </div>
        </div>
      </section>

      {/* ── FOOTER STRIP ── */}
      <footer style={s.footer}>
        <div style={s.footerInner}>
          <div style={s.footerBrand}>
            <div style={s.footerLogoBox}>
              <Zap size={18} color="#14532d" />
            </div>
            <span style={s.footerName}>Zynventics Technologies</span>
          </div>
          <p style={s.footerCopy}>
            © {new Date().getFullYear()} Zynventics Technologies. All rights
            reserved.
          </p>
          <a href="mailto:info@zynventics.com" style={s.footerEmail}>
            info@zynventics.com
          </a>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
